import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { MapPin, Plus, Trash2, Edit2, Check, Home } from "lucide-react";
import { toast } from "sonner";
import { API } from "../constants";
import { useAuth } from "../context/AuthContext";

const blankAddress = { label: "", name: "", street: "", city: "", state: "", zip: "", phone: "", country: "US", is_default: false };

const AddressBookPage = () => {
  const { user, token } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blankAddress);
  const [showForm, setShowForm] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const load = async () => {
    if (!user) { setLoading(false); return; }
    try {
      const r = await axios.get(`${API}/addresses`, { headers });
      setAddresses(r.data.addresses || []);
    } catch {
      toast.error("Failed to load addresses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  const startNew = () => {
    setForm(blankAddress);
    setEditingId(null);
    setShowForm(true);
  };

  const startEdit = (a) => {
    setForm({ ...blankAddress, ...a });
    setEditingId(a.address_id);
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/addresses/${editingId}`, form, { headers });
        toast.success("Address updated");
      } else {
        await axios.post(`${API}/addresses`, form, { headers });
        toast.success("Address saved");
      }
      setShowForm(false);
      setForm(blankAddress);
      setEditingId(null);
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this address?")) return;
    try {
      await axios.delete(`${API}/addresses/${id}`, { headers });
      load();
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#050505] pt-24 px-4 text-center">
        <div className="max-w-md mx-auto py-16">
          <p className="text-gray-400 mb-4">Log in to manage your address book.</p>
          <Link to="/login" className="bg-[#D62828] hover:bg-[#B91C1C] text-white px-6 py-3 rounded-sm font-bold uppercase tracking-wider">Log In</Link>
        </div>
      </div>
    );
  }

  const inputCls = "w-full bg-[#1A1A1A] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:border-[#D62828] focus:outline-none";

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-16" data-testid="address-book-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-4xl tracking-wide">ADDRESS BOOK</h1>
            <p className="text-gray-500 text-sm mt-1">{addresses.length} saved address{addresses.length === 1 ? '' : 'es'}</p>
          </div>
          {!showForm && (
            <button onClick={startNew} className="bg-[#D62828] hover:bg-[#B91C1C] text-white px-4 py-2 rounded-sm text-sm font-bold uppercase tracking-wide flex items-center gap-2" data-testid="add-address-btn">
              <Plus className="w-4 h-4" /> Add Address
            </button>
          )}
        </div>

        {showForm && (
          <form onSubmit={submit} className="bg-[#121212] border border-white/10 rounded-sm p-5 mb-6" data-testid="address-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <input className={inputCls} placeholder="Label (e.g. Home)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} required data-testid="addr-label" />
              <input className={inputCls} placeholder="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="addr-name" />
              <input className={`${inputCls} sm:col-span-2`} placeholder="Street Address" value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })} required data-testid="addr-street" />
              <input className={inputCls} placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} required data-testid="addr-city" />
              <input className={inputCls} placeholder="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} required data-testid="addr-state" />
              <input className={inputCls} placeholder="ZIP" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} required data-testid="addr-zip" />
              <input className={inputCls} placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="addr-phone" />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-300 mb-3">
              <input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} className="w-4 h-4 accent-[#D62828]" />
              Set as default shipping address
            </label>
            <div className="flex gap-2">
              <button type="submit" className="bg-[#D62828] hover:bg-[#B91C1C] text-white px-5 py-2 rounded-sm text-xs font-bold uppercase tracking-wide" data-testid="save-address-btn">
                <Check className="w-4 h-4 inline mr-1" /> {editingId ? 'Update' : 'Save'}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="text-gray-400 hover:text-white text-xs px-3 py-2">Cancel</button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D62828] mx-auto"></div>
        ) : addresses.length === 0 && !showForm ? (
          <div className="text-center py-16">
            <MapPin className="w-16 h-16 text-gray-700 mx-auto mb-4" />
            <p className="text-gray-400 mb-6">No saved addresses yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="address-list">
            {addresses.map((a) => (
              <div key={a.address_id} className="bg-[#121212] border border-white/10 rounded-sm p-4" data-testid={`address-${a.address_id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Home className="w-4 h-4 text-[#D62828]" />
                    <p className="text-white font-semibold">{a.label}</p>
                    {a.is_default && <span className="text-xs px-2 py-0.5 bg-[#D4AF37]/20 text-[#D4AF37] rounded-sm uppercase tracking-wide">Default</span>}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => startEdit(a)} className="text-gray-500 hover:text-white p-1" title="Edit"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => remove(a.address_id)} className="text-gray-500 hover:text-red-400 p-1" title="Delete"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="text-sm text-gray-300 leading-relaxed">
                  <p>{a.name}</p>
                  <p>{a.street}</p>
                  <p>{a.city}, {a.state} {a.zip}</p>
                  {a.phone && <p className="text-gray-500">{a.phone}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddressBookPage;
